// components/CustomerWelfareHandle.jsx

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Grid,
} from '@mui/material';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { API_BASE_URL } from '../../constants/constants';
import { useParams } from 'react-router-dom';
import {jwtDecode} from 'jwt-decode';
import BackButton from '../../components/BackButton';

const CustomerWelfareHandle = () => {
  const [balances, setBalances] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'add' or 'withdraw'
  const [selectedVpa, setSelectedVpa] = useState('');
  const [amount, setAmount] = useState('');
  const [comments, setComments] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [customerName, setCustomerName] = useState(''); // ← Added customerName state

  const { customerId } = useParams();

  useEffect(() => {
    fetchBalances();
  }, [customerId]);

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');

      const response = await axios.post(
        `${API_BASE_URL}/customerReport/welfareBalance`,
        {
          customerId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const data = response.data;

        // ← Extract customerName from API response
        setCustomerName(data.customerName || '');

        if (data.balances && data.balances.length > 0) {
          const balancesData = data.balances.map((item, index) => ({
            id: index + 1,
            serialNumber: index + 1,
            customerName: data.customerName || '', // ← Add customerName to each row
            vpa: item.vpa,
            totalAmount: parseFloat(item.totalAmount).toFixed(2),
          }));

          setBalances(balancesData);
          setGrandTotal(parseFloat(data.grandTotal).toFixed(2));
        } else {
          setBalances([]);
          setGrandTotal(0);
        }
      } else {
        setBalances([]);
        setGrandTotal(0);
      }
    } catch (error) {
      if (error.response) {
        setDialogMessage(
          error.response.data.message ||
            'An error occurred while fetching data.'
        );
        setOpenMessageDialog(true);
      }
      setBalances([]);
      setGrandTotal(0);
    }
    setLoading(false);
  };

  const handleDialogOpen = (type, vpa) => {
    setDialogType(type);
    setSelectedVpa(vpa);
    setAmount('');
    setComments('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleTransaction = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      setDialogMessage('Please enter a valid amount.');
      setOpenMessageDialog(true);
      return;
    }

    setDialogOpen(false);
    setLoading(true);

    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      const endpoint =
        dialogType === 'add'
          ? `${API_BASE_URL}/welfareFundHandle/add`
          : `${API_BASE_URL}/welfareFundHandle/withdraw`;

      const response = await axios.post(
        endpoint,
        {
          userId,
          vpaId: selectedVpa,
          amount: parseFloat(amount),
          comments,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setDialogMessage(response.data.message);
        setOpenMessageDialog(true);
        fetchBalances();
      } else {
        setDialogMessage('An error occurred. Please try again.');
        setOpenMessageDialog(true);
      }
    } catch (error) {
      if (error.response) {
        setDialogMessage(
          error.response.data.message || 'An error occurred.'
        );
        setOpenMessageDialog(true);
      } else {
        setDialogMessage('An error occurred. Please try again.');
        setOpenMessageDialog(true);
      }
    }

    setLoading(false);
  };

  const columns = [
    { 
      field: 'serialNumber', 
      headerName: 'S.No.', 
      width: 70,
      align: 'center',
      headerAlign: 'center'
    },
    { // ← Added customerName column
      field: 'customerName', 
      headerName: 'Customer', 
      width: 180,
      align: 'left',
      headerAlign: 'center'
    },
    { 
      field: 'vpa', 
      headerName: 'VPA', 
      width: 200,
      align: 'left',
      headerAlign: 'center'
    },
    { 
      field: 'totalAmount', 
      headerName: 'Total Amount (₹)', 
      width: 150,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => `₹${params.value}` // ← Added currency formatting
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 400,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleDialogOpen('add', params.row.vpa)}
            sx={{ 
              minWidth: '120px',
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Add Welfare
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleDialogOpen('withdraw', params.row.vpa)}
            sx={{ 
              minWidth: '140px',
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Withdraw Welfare
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
       <Box sx={{ position: 'relative', padding: 2 }}>
                    {/* Place BackButton outside the Grid */}
                    <BackButton />
                    <br/>  <br/>
      <Typography variant="h5" align="center" gutterBottom>
        Customer Welfare Handle
      </Typography>
      {/* ← Added customerName display in UI */}
      {customerName && (
        <Typography variant="subtitle1" align="center" gutterBottom>
          Customer: <strong>{customerName}</strong>
        </Typography>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: '10px' }}>
            Loading welfare balances...
          </Typography>
        </div>
      ) : (
        <div>
          {balances && balances.length > 0 ? (
            <>
              <Paper elevation={2} style={{ marginBottom: '20px' }}>
                <div style={{ height: 600, width: '100%' }}>
                  <DataGrid
                    rows={balances}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 20, 50]}
                    pagination
                    disableSelectionOnClick
                    autoHeight={false}
                    sx={{
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto',
                      },
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #e0e0e0',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f5f5f5',
                        borderBottom: '2px solid #1976d2',
                      },
                    }}
                  />
                </div>
              </Paper>
              
              <Paper elevation={3} style={{ padding: '15px', marginTop: '20px' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      <strong>Total VPAs:</strong> {balances.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" align="right" style={{ color: '#1976d2', fontWeight: 'bold' }}>
                      Grand Total: ₹{grandTotal}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </>
          ) : (
            <Paper elevation={2} style={{ padding: '40px', textAlign: 'center', marginTop: '20px' }}>
              <Typography variant="h6" color="textSecondary">
                No welfare balances found.
              </Typography>
              <Typography variant="body2" style={{ marginTop: '10px' }}>
                This customer does not have any welfare balance records.
              </Typography>
            </Paper>
          )}
        </div>
      )}

      {/* Dialog for Add/Withdraw Welfare */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: dialogType === 'add' ? '#1976d2' : '#9c27b0',
          color: 'white',
          textAlign: 'center'
        }}>
          {dialogType === 'add' ? 'Add Welfare Fund' : 'Withdraw Welfare Fund'}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
              VPA: <span style={{ color: '#1976d2' }}>{selectedVpa}</span>
            </Typography>
            {customerName && (
              <Typography variant="subtitle2" sx={{ color: '#666', mt: 1 }}>
                Customer: <span style={{ fontWeight: 'bold' }}>{customerName}</span>
              </Typography>
            )}
          </Box>
          <TextField
            label="Amount (₹)"
            fullWidth
            margin="normal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Comments"
            fullWidth
            margin="normal"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            multiline
            rows={3}
            placeholder="Enter comments for this transaction..."
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleDialogClose} 
            color="primary"
            variant="outlined"
            sx={{ minWidth: '100px' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleTransaction} 
            color="primary"
            variant="contained"
            sx={{ 
              minWidth: '100px',
              backgroundColor: dialogType === 'add' ? '#1976d2' : '#9c27b0'
            }}
          >
            Confirm {dialogType === 'add' ? 'Add' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Messages */}
      <Dialog
        open={openMessageDialog}
        onClose={() => setOpenMessageDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Alert</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenMessageDialog(false)} 
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </div>
  );
};

export default CustomerWelfareHandle;